import RaceVideo from './RaceVideo';
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { createReservation, findRaceByCode, isSupabaseConfigured, listRaceCategories, listRaceInstances, verifyReservation, getAssetUrl } from '../lib/supabaseClient';
import { RaceCategory, RaceInstance, RaceLookupResult, ReservationPayload } from '../types';
import { RemoveScroll } from 'react-remove-scroll';

interface PilotosLoginProps {
  onClose: () => void;
}

// Validación de Cédula Uruguaya (formato simple)
const isValidCI = (ci: string) => {
  const cleanCI = ci.replace(/\D/g, '');
  return cleanCI.length >= 7 && cleanCI.length <= 8;
};

// Validación de Teléfono Uruguayo
const isValidPhone = (phone: string) => {
  const cleanPhone = phone.replace(/\D/g, '');
  return cleanPhone.startsWith('09') && cleanPhone.length === 9; // Celulares
};

const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isAllowedDay = (dateStr: string) => {
  const date = new Date(dateStr + 'T12:00:00'); // Evitar problemas de zona horaria
  const day = date.getDay();
  // 0: Domingo, 4: Jueves, 5: Viernes, 6: Sábado
  return [0, 4, 5, 6].includes(day);
};

const getTodayDate = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

const today = getTodayDate();

const defaultReservation: Omit<ReservationPayload, 'p_race_instance_id'> & { p_race_instance_id: string } = {
  p_race_instance_id: '',
  p_full_name: '',
  p_email: '',
  p_phone: '',
  p_identification: '',
  p_seats_requested: 1,
  p_participant_names: [''],
  p_notes: '',
};

export const PilotosLogin: React.FC<PilotosLoginProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<'reserve' | 'lookup'>('reserve');
  const [reservationStep, setReservationStep] = useState<'form' | 'verify' | 'confirmed'>('form');
  const [pendingBookingId, setPendingBookingId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmedCode, setConfirmedCode] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(today);
  const [raceCategories, setRaceCategories] = useState<RaceCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [instances, setInstances] = useState<RaceInstance[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState('');
  const [reservation, setReservation] = useState(defaultReservation);
  const [lookupCode, setLookupCode] = useState('');
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupResult, setLookupResult] = useState<RaceLookupResult | null>(null);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const selectedInstance = useMemo(
    () => instances.find((instance) => instance.id === selectedInstanceId) ?? null,
    [instances, selectedInstanceId],
  );

  const selectedCategoryObj = useMemo(
    () => raceCategories.find((c) => c.id === selectedCategory) ?? null,
    [raceCategories, selectedCategory],
  );

  function instanceCapacity(inst: RaceInstance) {
    return inst.capacity_total || selectedCategoryObj?.capacity_per_instance || 4;
  }

  const seatsAvailable = selectedInstance
    ? Math.max(instanceCapacity(selectedInstance) - selectedInstance.seats_reserved, 0)
    : (selectedCategoryObj?.capacity_per_instance || 4);

  const maxSeats = selectedInstance 
    ? Math.min(seatsAvailable, instanceCapacity(selectedInstance)) 
    : (selectedCategoryObj?.capacity_per_instance || 4);

  const canReserve = Boolean(
    reservation.p_full_name.trim() &&
      isValidEmail(reservation.p_email) &&
      isValidCI(reservation.p_identification) &&
      isValidPhone(reservation.p_phone) &&
      selectedInstanceId &&
      reservation.p_seats_requested > 0 &&
      reservation.p_seats_requested <= (selectedInstance ? seatsAvailable : 4) &&
      reservation.p_participant_names.filter((name) => name.trim()).length === reservation.p_seats_requested &&
      selectedDate >= today
  );

  const isPastDate = selectedDate < today;

  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      if (!isSupabaseConfigured) return;
      setLoadingCategories(true);
      try {
        const { data, error } = await listRaceCategories();
        if (data && data.length > 0) {
          setRaceCategories(data);
          setSelectedCategory(data[0].id);
        } else {
          console.warn('Using fallback categories due to error or empty data:', error);
          const fallback: RaceCategory[] = [
            { id: 'racing', label: 'Racing Session // 30 min', capacity_per_instance: 4, duration_minutes: 30, description: '' },
            { id: 'race', label: 'Carrera Velocidad // 15 min', capacity_per_instance: 6, duration_minutes: 15, description: '' },
            { id: 'kids', label: 'Kids Mode // 20 min', capacity_per_instance: 4, duration_minutes: 20, description: '' },
          ];
          setRaceCategories(fallback);
          setSelectedCategory(fallback[0].id);
        }
      } catch (e) {
        console.error('Unexpected error loading categories:', e);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const loadInstances = async () => {
      if (!isSupabaseConfigured || !selectedCategory) return;
      
      if (!isAllowedDay(selectedDate)) {
        setInstances([]);
        setValidationError('Atención: Solo operamos de Jueves a Domingos.');
        return;
      }

      if (selectedDate < today) {
        setInstances([]);
        setValidationError('La fecha seleccionada ya pasó.');
        return;
      }

      setValidationError(null);
      setLoadingInstances(true);
      setError(null);
      const { data, error: listError } = await listRaceInstances(selectedDate, selectedCategory);
      if (listError) {
        setError(`Error de conexión: ${listError}`);
        setInstances([]);
      } else {
        // Filtrar por horario de 18:00 a 23:30
        // Y si es hoy, filtrar horarios pasados
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinutes = now.getMinutes();
        const currentTotalMins = currentHour * 60 + currentMinutes;

        const filtered = (data ?? []).filter(inst => {
          const hour = parseInt(inst.scheduled_time.split(':')[0]);
          const mins = parseInt(inst.scheduled_time.split(':')[1]);
          const totalMins = hour * 60 + mins;

          // Filtro de rango operativo
          const inRange = totalMins >= 18 * 60 && totalMins <= 23 * 60 + 30;
          
          // Filtro de tiempo pasado si es hoy
          let isFuture = true;
          if (selectedDate === today) {
             isFuture = totalMins > currentTotalMins;
          }

          return inRange && isFuture;
        });
        setInstances(filtered);
      }
      setSelectedInstanceId('');
      setLoadingInstances(false);
    };

    loadInstances();
  }, [selectedDate, selectedCategory, refreshTrigger]);

  const updateParticipant = (index: number, value: string) => {
    setReservation((prev) => {
      const next = [...prev.p_participant_names];
      next[index] = value;
      return { ...prev, p_participant_names: next };
    });
  };

  const updateSeatsRequested = (seats: number) => {
    setReservation((prev) => {
      const safeSeats = Math.max(1, seats);
      const names = [...prev.p_participant_names];
      while (names.length < safeSeats) names.push('');
      return { ...prev, p_seats_requested: safeSeats, p_participant_names: names.slice(0, safeSeats) };
    });
  };

  const submitReservation = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canReserve) return;

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    const { data, error: reservationError } = await createReservation({
      ...reservation,
      p_race_instance_id: selectedInstanceId,
      p_participant_names: reservation.p_participant_names.map((name) => name.trim()),
    });

    if (reservationError) {
      setError(`Error: ${reservationError}`);
      setSubmitting(false);
      return;
    }

    setPendingBookingId(data?.booking_id || null);
    setReservationStep('verify');
    setSubmitting(false);
  };

  const submitVerification = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!pendingBookingId || !verificationCode.trim()) return;

    setSubmitting(true);
    setError(null);

    const { data, error: verifyError } = await verifyReservation(pendingBookingId, verificationCode.trim());

    if (verifyError) {
      setError(`Error: ${verifyError}`);
      setSubmitting(false);
      return;
    }

    setConfirmedCode(data?.access_code || '');
    setReservation(defaultReservation);
    setReservationStep('confirmed');
    setPendingBookingId(null);
    setVerificationCode('');
    setSubmitting(false);
  };

  const resetToForm = () => {
    setReservationStep('form');
    setConfirmedCode(null);
    setSuccess(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleLookup = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!lookupCode.trim() || !lookupEmail.trim()) return;

    setSearching(true);
    setError(null);
    setSuccess(null);
    setLookupResult(null);

    const { data, error: lookupError } = await findRaceByCode(lookupCode.trim().toUpperCase(), lookupEmail.trim());

    if (lookupError) {
      setError(`Error: ${lookupError}`);
      setSearching(false);
      return;
    }

    if (!data) {
      setError('No encontramos datos para esa combinación de código y email.');
      setSearching(false);
      return;
    }

    setLookupResult(data);
    setSearching(false);
  };

  return (
    <RemoveScroll>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/80 flex flex-col items-center justify-center p-2 sm:p-4 overflow-hidden font-racing touch-none overscroll-none"
      >
        <div className="fixed inset-0 pointer-events-none opacity-20 bg-noise" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative border border-cyan-400/30 bg-black/95 w-full max-w-5xl p-4 sm:p-6 md:p-10 max-h-[90vh] overflow-y-auto overflow-x-hidden touch-pan-y overscroll-none"
        >
        <div className="flex flex-row justify-between items-start gap-4 mb-6">
          <div>
            <p className="text-cyan-400/70 text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase">TurVolt // Booking Flow</p>
            <h3 className="text-lg sm:text-2xl md:text-4xl font-black text-white italic mt-1 sm:mt-2 leading-tight">Reservá cupo o consultá tu carrera</h3>
          </div>
          <button
            onClick={onClose}
            className="border border-white/30 text-white px-3 py-2 sm:px-4 sm:py-2 text-[10px] sm:text-xs uppercase hover:bg-white hover:text-black transition-colors shrink-0"
          >
            Cerrar
          </button>
        </div>

        {!isSupabaseConfigured && (
          <p className="mb-4 text-xs uppercase text-yellow-300 border border-yellow-500/50 bg-yellow-500/10 p-3">
            Configurá las variables de entorno para activar el sistema de reservas.
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
          <button
            onClick={() => setActiveTab('reserve')}
            className={`px-4 py-2 text-xs uppercase border ${
              activeTab === 'reserve' ? 'border-cyan-400 text-cyan-300' : 'border-white/20 text-gray-500'
            }`}
          >
            Reservar horario
          </button>
          <button
            onClick={() => setActiveTab('lookup')}
            className={`px-4 py-2 text-xs uppercase border ${
              activeTab === 'lookup' ? 'border-cyan-400 text-cyan-300' : 'border-white/20 text-gray-500'
            }`}
          >
            Tengo código / Ver carrera
          </button>
        </div>

        {activeTab === 'reserve' && (
          reservationStep === 'confirmed' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-10 space-y-8 text-center"
            >
              <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center border border-green-500 animate-pulse">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <div className="space-y-2">
                <h2 className="text-3xl md:text-4xl font-black text-white italic uppercase tracking-tighter">
                  ¡Reserva Confirmada!
                </h2>
                <p className="text-gray-400 text-sm uppercase tracking-wide">
                  Tu lugar en la parrilla está asegurado.
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 p-6 rounded-lg w-full max-w-md space-y-4 relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                
                <p className="text-xs text-cyan-400 uppercase tracking-widest font-bold">Código de Acceso</p>
                <div className="text-5xl md:text-6xl font-black text-white tracking-[0.2em] font-mono select-all">
                  {confirmedCode}
                </div>
                <p className="text-[10px] text-gray-500 uppercase">
                  Presenta este código al llegar al circuito.
                </p>
              </div>

              <div className="space-y-4 w-full max-w-xs">
                <p className="text-xs text-green-400/80 uppercase">
                  Hemos enviado los detalles a tu correo.
                </p>
                <button
                  onClick={resetToForm}
                  className="w-full px-6 py-4 border border-white/20 text-white hover:bg-white/10 uppercase text-xs tracking-widest transition-colors"
                >
                  Realizar otra reserva
                </button>
              </div>
            </motion.div>
          ) : reservationStep === 'form' ? (
            <form onSubmit={submitReservation} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <label className="text-xs uppercase text-gray-300 flex flex-col gap-2">
                Fecha de carrera
                <div className="relative">
                  <input
                    type="date"
                    min={today}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className={`w-full bg-black/70 border ${!isAllowedDay(selectedDate) || isPastDate ? 'border-red-500' : 'border-white/20'} px-3 py-3 text-white uppercase`}
                    style={{ colorScheme: 'dark' }}
                  />
                </div>
                {!isAllowedDay(selectedDate) && <span className="text-[10px] text-red-500">Solo Jueves a Domingos</span>}
                {isPastDate && <span className="text-[10px] text-red-500">La fecha debe ser igual o posterior a hoy</span>}
              </label>
              <label className="text-xs uppercase text-gray-300 flex flex-col gap-2">
                Modalidad
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={loadingCategories}
                  className="bg-black/70 border border-white/20 px-3 py-3 text-white disabled:opacity-50"
                >
                  {loadingCategories ? (
                    <option>Cargando modalidades...</option>
                  ) : (
                    raceCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.label}
                      </option>
                    ))
                  )}
                </select>
              </label>

              <label className="md:col-span-2 text-xs uppercase text-gray-300 flex flex-col gap-2 min-w-0">
                Horario disponible
                <select
                  value={selectedInstanceId}
                  onChange={(e) => setSelectedInstanceId(e.target.value)}
                  disabled={loadingInstances || instances.length === 0}
                  className="bg-black/70 border border-white/20 px-3 py-3 text-white disabled:opacity-50"
                >
                  <option value="">
                    {loadingInstances ? 'Cargando horarios...' : 
                     instances.length === 0 ? 'No hay horarios disponibles' : 
                     'Seleccioná una carrera abierta'}
                  </option>
                  {instances.map((instance) => {
                    const free = instanceCapacity(instance) - instance.seats_reserved;
                    return (
                      <option key={instance.id} value={instance.id}>
                        {instance.scheduled_time.slice(0, 5)} · {free} lugares libres de {instanceCapacity(instance)}
                      </option>
                    );
                  })}
                </select>
                {selectedInstance && (
                  <span className="text-[10px] text-cyan-400 uppercase tracking-wide">
                    Cupos: {seatsAvailable} disponibles / {instanceCapacity(selectedInstance)} totales.
                  </span>
                )}
              </label>

              <label className="text-xs uppercase text-gray-300 flex flex-col gap-2">
                Nombre completo
                <input
                  value={reservation.p_full_name}
                  onChange={(e) => setReservation((prev) => ({ ...prev, p_full_name: e.target.value }))}
                  className="bg-black/70 border border-white/20 px-3 py-3 text-white"
                />
              </label>
              <label className="text-xs uppercase text-gray-300 flex flex-col gap-2">
                Cédula de Identidad
                <input
                  value={reservation.p_identification}
                  placeholder="Ej: 12345678"
                  onChange={(e) => setReservation((prev) => ({ ...prev, p_identification: e.target.value }))}
                  className={`bg-black/70 border ${reservation.p_identification && !isValidCI(reservation.p_identification) ? 'border-red-500' : 'border-white/20'} px-3 py-3 text-white`}
                />
                {reservation.p_identification && !isValidCI(reservation.p_identification) && <span className="text-[10px] text-red-500">Formato inválido (7-8 dígitos)</span>}
              </label>

              <label className="text-xs uppercase text-gray-300 flex flex-col gap-2">
                Email
                <input
                  type="email"
                  value={reservation.p_email}
                  onChange={(e) => setReservation((prev) => ({ ...prev, p_email: e.target.value }))}
                  className={`bg-black/70 border ${reservation.p_email && !isValidEmail(reservation.p_email) ? 'border-red-500' : 'border-white/20'} px-3 py-3 text-white`}
                />
              </label>
              <label className="text-xs uppercase text-gray-300 flex flex-col gap-2">
                Celular
                <input
                  placeholder="Ej: 099123456"
                  value={reservation.p_phone}
                  onChange={(e) => setReservation((prev) => ({ ...prev, p_phone: e.target.value }))}
                  className={`bg-black/70 border ${reservation.p_phone && !isValidPhone(reservation.p_phone) ? 'border-red-500' : 'border-white/20'} px-3 py-3 text-white`}
                />
                {reservation.p_phone && !isValidPhone(reservation.p_phone) && <span className="text-[10px] text-red-500">Debe empezar con 09 y tener 9 dígitos</span>}
              </label>

              <label className="text-xs uppercase text-gray-300 flex flex-col gap-2">
                Cupos a reservar (Máx {maxSeats})
                <div className="flex items-center gap-2">
                  {Array.from({ length: maxSeats }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => updateSeatsRequested(num)}
                      className={`flex-1 py-3 border ${
                        reservation.p_seats_requested === num
                          ? 'border-cyan-400 bg-cyan-400/20 text-cyan-300'
                          : 'border-white/20 bg-black/70 text-gray-400 hover:border-white/40'
                      } transition-colors font-bold`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
                {selectedInstance && reservation.p_seats_requested > seatsAvailable && (
                  <span className="text-[10px] text-red-500 uppercase">Solo quedan {seatsAvailable} lugares</span>
                )}
              </label>

              <label className="text-xs uppercase text-gray-300 flex flex-col gap-2 md:col-span-2">
                Nombres de participantes ({reservation.p_seats_requested})
                <div className="space-y-2">
                  {reservation.p_participant_names.map((name, index) => (
                    <input
                      key={`participant-${index}`}
                      value={name}
                      onChange={(e) => updateParticipant(index, e.target.value)}
                      placeholder={`Piloto ${index + 1}`}
                      className="w-full bg-black/70 border border-white/20 px-3 py-2 text-white"
                    />
                  ))}
                </div>
              </label>

              <label className="md:col-span-2 text-xs uppercase text-gray-300 flex flex-col gap-2">
                Notas
                <textarea
                  rows={3}
                  value={reservation.p_notes}
                  onChange={(e) => setReservation((prev) => ({ ...prev, p_notes: e.target.value }))}
                  className="bg-black/70 border border-white/20 px-3 py-3 text-white"
                />
              </label>

              <button
                type="submit"
                disabled={submitting || !canReserve}
                className="md:col-span-2 px-6 py-3 border border-cyan-400 text-cyan-300 disabled:opacity-40 hover:bg-cyan-400 hover:text-black uppercase text-xs"
              >
                {submitting ? 'Reservando...' : 'Reservar cupos'}
              </button>
            </form>
          ) : (
            <form onSubmit={submitVerification} className="flex flex-col items-center justify-center py-10 space-y-6">
              <div className="text-center space-y-2">
                <h4 className="text-cyan-400 font-bold uppercase tracking-widest">Verifica tu Email</h4>
                <p className="text-gray-400 text-xs uppercase">Hemos enviado un código de 6 dígitos a {reservation.p_email}</p>
              </div>
              
              <input
                type="text"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="bg-black/70 border border-cyan-400/50 text-center text-4xl font-black tracking-[0.5em] py-4 w-full max-w-xs text-white focus:border-cyan-400 outline-none"
              />

              <div className="flex flex-col w-full max-w-xs gap-3">
                <button
                  type="submit"
                  disabled={submitting || verificationCode.length < 6}
                  className="w-full px-6 py-4 bg-cyan-400 text-black font-bold uppercase text-xs hover:bg-cyan-300 disabled:opacity-50"
                >
                  {submitting ? 'Verificando...' : 'Confirmar Reserva'}
                </button>
                <button
                  type="button"
                  onClick={resetToForm}
                  className="text-gray-500 text-[10px] uppercase hover:text-white"
                >
                  Volver a editar datos
                </button>
              </div>
            </form>
          )
        )}

        {activeTab === 'lookup' && (
          <div>
            <form onSubmit={handleLookup} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <label className="text-xs uppercase text-gray-300 flex flex-col gap-2">
                Código de reserva / carrera
                <input
                  value={lookupCode}
                  onChange={(e) => setLookupCode(e.target.value.toUpperCase())}
                  className="bg-black/70 border border-white/20 px-3 py-3 text-white"
                />
              </label>
              <label className="text-xs uppercase text-gray-300 flex flex-col gap-2">
                Email de reserva
                <input
                  type="email"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  className="bg-black/70 border border-white/20 px-3 py-3 text-white"
                />
              </label>
              <button
                type={lookupResult ? "button" : "submit"}
                onClick={() => {
                  if (lookupResult) {
                     setLookupResult(null);
                     setLookupCode('');
                     setLookupEmail('');
                     setError(null);
                     setSuccess(null);
                  }
                }}
                disabled={searching}
                className="md:col-span-2 px-6 py-3 border border-white text-white hover:bg-white hover:text-black uppercase text-xs transition-colors font-bold"
              >
                {searching ? 'Consultando...' : (lookupResult ? 'Consultar otro código' : 'Ver carrera')}
              </button>
            </form>

            {lookupResult && (
              <div className="border border-white/10 p-4 space-y-3">
                <p className="text-cyan-300 text-xs uppercase tracking-wide">
                  Estado: {lookupResult.status} · {lookupResult.scheduled_date} {lookupResult.scheduled_time?.slice(0, 5)}
                </p>
                <p className="text-white text-sm uppercase">Modalidad: {lookupResult.race_category_label}</p>

                {lookupResult.leaderboard?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left border border-white/10 min-w-[560px]">
                    <thead className="bg-white/5 text-cyan-300 uppercase">
                      <tr>
                        <th className="p-2">Pos</th>
                        <th className="p-2">Piloto</th>
                        <th className="p-2">Mejor vuelta</th>
                        <th className="p-2">Tiempo total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lookupResult.leaderboard.map((row) => (
                        <tr key={`${row.rank}-${row.pilot}`} className="border-t border-white/10 text-gray-200">
                          <td className="p-2">{row.rank}</td>
                          <td className="p-2">{row.pilot}</td>
                          <td className="p-2">{row.best_lap}</td>
                          <td className="p-2">{row.total_time}</td>
                        </tr>
                      ))}
                    </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 uppercase">Todavía no hay resultados publicados.</p>
                )}

                {lookupResult.video_url ? (
                  <RaceVideo url={lookupResult.video_url} />
                ) : (
                  <p className="text-xs text-gray-500 uppercase">Video aún no disponible.</p>
                )}
              </div>
            )}
          </div>
        )}

        {error && <p className="mt-4 text-xs uppercase text-red-400">{error}</p>}
        {success && <p className="mt-4 text-xs uppercase text-green-400">{success}</p>}
      </motion.div>
    </motion.div>
    </RemoveScroll>
  );
};
